import { useState, useRef, useEffect } from 'react';

/**
 * C5: one help affordance per screen.
 *
 * The alternative considered was a `?` next to every field, which turns into a rash of
 * punctuation across the form and still leaves nobody knowing what the screen as a whole
 * is for. One `i` per tab, opening two or three sentences and a link to the matching
 * section of the public docs, is a single affordance in a predictable place.
 *
 * **Help is never the only place a constraint is stated.** Limits, formats and file-size
 * caps belong next to the input that enforces them — anything only discoverable by
 * opening this panel is, in practice, undiscoverable. What lives here is orientation:
 * what this screen is for and what to do first.
 */

const DOCS_BASE = 'https://www.celsian.ai/docs';

/** Keyed by the tab's href, so a new tab without help simply renders nothing. */
const HELP: Record<string, { title: string; body: string[]; anchor: string }> = {
  settings: {
    title: 'Settings',
    body: [
      'The name and description are yours — visitors never see them.',
      'The AI model decides what each answer costs. The default is the cheapest capable option; the picker under Advanced shows how many conversations your plan buys on each one.',
    ],
    anchor: 'getting-started',
  },
  knowledge: {
    title: 'Knowledge',
    body: [
      'What your chatbot is allowed to read. Add a website by URL, upload documents, or both — they feed the same bot.',
      'Nothing updates on its own. After you add or remove a source, press Update knowledge; a bot answers from what it read the last time you asked it to read.',
    ],
    anchor: 'knowledge',
  },
  test: {
    title: 'Test',
    body: [
      'A private conversation with your chatbot, billed the same as a real one.',
      'If answers look thin, check the Knowledge tab first — a source that is still being read is not yet answerable.',
    ],
    anchor: 'getting-started',
  },
  integration: {
    title: 'Integration',
    body: [
      'Where the chatbot appears: an embed snippet for any site, a WordPress plugin, or Discord.',
      'A chatbot only replies on domains you have approved, so an embed code copied from your site will not work on somebody else’s.',
    ],
    anchor: 'deploying',
  },
  stats: {
    title: 'Stats',
    body: [
      'Conversations and credits over time, so you can see what the chatbot is actually being asked.',
      'A chatbot nobody talks to costs nothing.',
    ],
    anchor: 'credits',
  },
};

export default function TabHelp({ section }: { section: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const help = HELP[section];

  // Dismiss on outside click and on Escape. Without both, a panel opened by accident
  // has to be dismissed by clicking the same small target again.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!help) return null;

  return (
    <div className="relative flex items-center" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-label={`About the ${help.title} tab`}
        aria-expanded={open}
        className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-slate-700 transition-colors"
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-9-3a1 1 0 112 0 1 1 0 01-2 0zm.75 3a.75.75 0 00-.75.75v3.5a.75.75 0 001.5 0v-3.5a.75.75 0 00-.75-.75z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={`${help.title} help`}
          className="absolute right-0 top-8 z-50 w-80 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-lg"
        >
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{help.title}</h4>
          {help.body.map((line, i) => (
            <p key={i} className="mt-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
              {line}
            </p>
          ))}
          <a
            href={`${DOCS_BASE}#${help.anchor}`}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-block text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
          >
            Read more in the docs →
          </a>
        </div>
      )}
    </div>
  );
}
